import { ErrorType } from "~/enums/ErrorType";
import { StringHelper } from "~/helpers/stringHelper";
import { INavigableValidatableModel } from "~/interfaces/navigableValidationInterfaces";
import { ValidationResultValueInterface } from "~/interfaces/validationResultInterface";
import { ErrorNavOpts } from "~/models/navigableValidations/navigableValidation";
import { ValidationModalSection } from "~/models/navigableValidations/validationSections";
import Validator from "~/models/validators/validator";
import ValidationError, { IErrorNavOpts, ValidationErrorLevel } from "../validationError";

type TConditionCallback<TModel> = (model: INavigableValidatableModel<any>, validatorKey?: string) => boolean;
type TValidatorCallback<TModel> = (value: any, model?: INavigableValidatableModel<TModel>, options?: object) => boolean;
type TDisplayNameCallback<TModel> = (key: string, model?: INavigableValidatableModel<TModel>) => string;

export interface INavigableValidatorOptions<TModel> {
  /**
   * Unique key for validator.
   * Doubles as field name for validators that are calling
   * `model[validatorKey]` to check a value
   */
  validatorKey?: string;
  /**
   * Section to display error under in validation errors modal
   */
  section: ValidationModalSection | string;
  /**
   * Message to display in hover tooltip in validation modal
   */
  message?: ((model: INavigableValidatableModel<any>, validatorKey: string) => string) | string ;
  /**
   * if set, will use as display name in errors modal
   */
  displayName?: TDisplayNameCallback<TModel> | string;
  /**
   * Options object that ValidationErrorHandler will use
   * to pass into AppRouter
   */
  errorNavOpts?: IErrorNavOpts;
  path?: string; // NavigableValidation will use to build path for navigation
  /**
   * ErrorType
   *
   * The ValidationErrorCollection organizes errors by type
   * so that the error modal displays all the errors of the same
   * type in the same section.
   */
  type?: ErrorType;
  /**
   * the scrollable selector the ValidationErrorHandler will use
   * in `ValidationErrrorHandler#reveal()` to find and go to the element
   */
  errorLevel?: ValidationErrorLevel;
  containerElementSelector?: string;
  targetElementId?: string;
  /**
   * if set, will use as value when creating route for ErrorNavOpts
   */
  anchorHash?: string;
  /**
   * Conditionals
   *
   * set this.conditionalField and this.conditionalValue to validate
   * only if model[conditionalField] === conditionalValue
   * note: validation will run if (this.conditionalField === null || undefined)
   *
   * set this.conditional to validate only if the function call returns true
   * note: validation will run if (this.conditional === null || undefined)
   *
   * set both this.conditionalField && this.conditional
   * and validation will run only if both conditions are met
   */
  conditionalField?: keyof TModel;
  conditionalValue?: any;
  conditional?: TConditionCallback<TModel>;
  /**
   * Custom Validator Method
   *
   * set validator on any child class
   * or set through constructor options
   * this must be set in order for the validator
   * to properly evaluate the validation state.
   *
   * If no validator is set or no custom _validate method is set
   * on the NavigableValidator, you will see a runtime error
   * encouraging you to set the validator method
   */
  validator?: TValidatorCallback<TModel>;
  /**
   * Addition options to pass into validator as last argument
   *
   * set validatorOptions on any child class
   * or set through constructor options
   */
  validatorOptions?: object;
  /**
   * `ValidationErrorHandler` Lifecycle Hooks
   *
   * After clicking into a validation error from the modal,
   * the navOpts from the error will be passed into `appRouter.push()`.
   *
   * Upon navigating to the new route, three methods will be awaited
   *
   * beforeReveal() - custom before callback that doesn't get called unless passed in as option
   * reveal()       - default method always gets called and can be overriden when passed as option
   * afterReveal()  - custom after callback that doesn't get called unless passed in as option
   */
  beforeReveal?: () => void;
  reveal?: (targetElementId: string, containerElementSelector: string, fallbackContainerSelector: string) => Promise<JQuery>;
  afterReveal?: ($el?: JQuery, ...args) => void;
}

interface INavigableValidator<T> {
  _validatorKey: string;
  errors: ValidationError[];
  options: INavigableValidatorOptions<T>;
  defaultMessage: string;
  defaultType: ErrorType;
  validator: TValidatorCallback<T>;
  afterReveal: () => void;
  beforeReveal: () => void;
  validatorKey: Pick<this, '_validatorKey'>
}

export default class NavigableValidator<T> implements INavigableValidator<T> {
  _validatorKey;
  errors = [];
  options;
  defaultMessage = "Invalid" ;
  defaultType = ErrorType.error;

  constructor(options: Partial<INavigableValidatorOptions<T>>) {
    this.options = options;
  }

  get section() {
    const section = this.options.section;
    if (!section) {
      throw new Error(`property 'section' must be set for ${this.constructor.name}.
      Include 'section' in validator options or set as _section on class`)
    }
    return StringHelper.capitalize(section);
  }

  get errorLevel() {
    return this.options.errorLevel || ValidationErrorLevel.Error
  }

  get beforeReveal() {
    return this.options.beforeReveal
  }

  get reveal() {
    return this.options.reveal
  }

  get afterReveal() {
    return this.options.afterReveal
  }

  set validator(validator: TValidatorCallback<T>){
    this.options.validator = validator;
  }

  get validator() {
    return this.options.validator || Validator.isValid
  }

  get validatorOptions() {
    return this.options.validatorOptions || {}
  }

  get validatorKey() {
    const key = this.options.validatorKey || this._validatorKey;
    if (!key) {
      throw new Error(`property validatorKey must be set for ${this.constructor.name}`)
    }
    return key
  }

  get field() {
    return this.validatorKey;
  }

  get isValid(): boolean {
    return this.errors.length === 0;
  }

  get containerElementSelector(): string {
    return this.options.containerElementSelector
  }

  get results(): ValidationResultValueInterface[] | ValidationResultValueInterface {
    return this.errors.length ? {
      [this.field]: {
        errors: this.errors,
        displayName: this.errors[0].displayName,
        errorNavOpts: this.errors[0].errorNavOpts
      }
    } : {};
  }

  shouldValidate(model) {
    const {conditionalField, conditionalValue, conditional} = this.options;
    return (!conditionalField || model[conditionalField] === conditionalValue) &&
    (!conditional || conditional(model, this.validatorKey))
  }

  public validate(model: INavigableValidatableModel<T>): boolean {
    this.errors = [];
    if (this.shouldValidate(model)) this._validate(model, this.validator);
    return this.isValid;
  }

  /**
   * The default `_validate` method is passed `this.validator`
   * make sure to add a custom `validator` function in your options
   * if you do not override the _validate method
   *
   * or
   *
   * You can overload this method in a class that extends `NavigableValidator`
   * and use custom logic to validate the model. This is a bulky approach and
   * should only be done when you're in need of a validator that will be used
   * commonly in other validations (eg. FieldPresenceValidator).
   */
  protected _validate(model: INavigableValidatableModel<T>, isValid: TValidatorCallback<T>): void {
    const value = model[this.field];
    if (!isValid(value, model, this.validatorOptions)) this.errors.push(this.buildValidationError(model, value, this.validatorKey));
  };

  buildValidationError(model: INavigableValidatableModel<T>, value?, validatorKey?, type?) {
    const messageFromOptions = this.options.message instanceof Function ? this.options.message(model, validatorKey) : this.options.message
    return new ValidationError({
      message: messageFromOptions ?? this.defaultMessage,
      displayName: this.errorDisplayName(model, validatorKey, value, type),
      errorNavOpts: this.errorNavOpts(model, validatorKey, value, type),
      field: validatorKey,
      type: this.options.type || type || this.defaultType,
      section: this.section,
      level: this.errorLevel,
    });
  }

  errorNavOpts(model, field, value, type?): IErrorNavOpts {
    if (model.validation.errorNavOpts) {
      return model.validation.errorNavOpts(this, this.validatorKey, value);
    } else {
      console.warn("implement errorNavOpts on caller validation, falling back to field key");
      return new ErrorNavOpts({})
    }
  }

  errorDisplayName(model, field, value, type) {
    if (this.options.displayName) {
      if (this.options.displayName instanceof Function) {
        return this.options.displayName(field, model);
      } else {
        return this.options.displayName;
      }
    } else {
      return model.getDisplayName(this.validatorKey, value);
    }
  }

}
